import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "hooks";
import {
  fetchSeriesList,
  setDestroy,
} from "containers/files-series/files-series-container.slice";
import { createSeries, updateSeries } from "services/series.service";
import {
  CatalogTable,
  ChangeLink,
  EditPanel,
  SettingsCard,
  StatusBanner,
  useSaveWithToast,
  useScrollIntoViewOnOpen,
} from "containers/settings/ui";
import {
  AddButton,
  Field,
  FIELD_INPUT_CLASS,
  PanelHeading,
} from "containers/settings/catalog-form-parts";

type SeriesDraft = { id?: string; title: string; description: string };

const NEW_SERIES: SeriesDraft = { title: "", description: "" };

// Sent by the server when a create hits a prefix the company already has.
const SERIES_ALREADY_EXISTS = "SERIES_ALREADY_EXISTS";

type SeriesEditPanelProps = {
  saved: SeriesDraft;
  companyId: string;
  onSaved: () => void;
  onCancel: () => void;
};

function SeriesEditPanel({
  saved,
  companyId,
  onSaved,
  onCancel,
}: SeriesEditPanelProps) {
  const { t } = useTranslation();
  const { save, saving, error } = useSaveWithToast();
  const [draft, setDraft] = useState(saved);
  const [triedToSave, setTriedToSave] = useState(false);
  const [renameWarned, setRenameWarned] = useState(false);

  const title = draft.title.trim();
  const isRename = saved.id !== undefined && title !== saved.title;
  const showRenameWarning = isRename && renameWarned;

  const submit = async () => {
    setTriedToSave(true);
    if (!title) return;
    // Renaming changes the numbers printed on documents already issued, so the
    // first click only asks; the second one (now "Yes, rename") saves.
    if (isRename && !renameWarned) {
      setRenameWarned(true);
      return;
    }

    const payload = { id: draft.id, title, description: draft.description };
    const wasSaved = await save(
      () =>
        draft.id
          ? updateSeries(payload, companyId)
          : createSeries(payload, companyId),
      {
        errorMessage: (err: any) =>
          err?.response?.data === SERIES_ALREADY_EXISTS
            ? t("series_already_exists")
            : t("settings_save_failed"),
      }
    );
    if (wasSaved) onSaved();
  };

  return (
    <EditPanel
      onSave={submit}
      onCancel={onCancel}
      saving={saving}
      saveLabel={
        showRenameWarning ? t("series_rename_confirm_button") : undefined
      }
      error={triedToSave && !title ? t("requiredFields") : error}
    >
      <PanelHeading>
        {saved.id ? `${t("settings_change")}: ${saved.title}` : t("series_add")}
      </PanelHeading>
      <div className="space-y-3">
        <Field htmlFor="series-title" label={t("series_prefix")}>
          <input
            id="series-title"
            className={FIELD_INPUT_CLASS}
            value={draft.title}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            autoComplete="off"
            autoFocus
          />
        </Field>
        <Field htmlFor="series-description" label={t("description")}>
          <textarea
            id="series-description"
            className={FIELD_INPUT_CLASS}
            rows={2}
            value={draft.description}
            onChange={(e) =>
              setDraft({ ...draft, description: e.target.value })
            }
          />
        </Field>
        {showRenameWarning && (
          <StatusBanner tone="warn" title={t("series_rename_warning")} />
        )}
      </div>
    </EditPanel>
  );
}

function SeriesContainer() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { companyId } = useParams<{ companyId: string }>();
  const { collection, isLoading, isError } = useAppSelector(
    (state) => state.seriesReducer
  );
  const [editing, setEditing] = useState<SeriesDraft | null>(null);
  const panelRef = useScrollIntoViewOnOpen(editing);

  useEffect(() => {
    if (companyId) dispatch(fetchSeriesList(companyId));
    return () => {
      dispatch(setDestroy());
    };
  }, [companyId, dispatch]);

  if (!companyId) return null;

  const startEdit = (id: string) => {
    const row = collection.find((series) => series.id === id);
    if (row) {
      setEditing({
        id: row.id,
        title: row.title,
        description: row.description ?? "",
      });
    }
  };

  const closeAndReload = () => {
    setEditing(null);
    dispatch(fetchSeriesList(companyId));
  };

  return (
    <SettingsCard
      title={t("settings_nav_series_label")}
      description={t("series_card_description")}
      actions={
        <AddButton
          label={t("series_add")}
          onClick={() => setEditing(NEW_SERIES)}
        />
      }
    >
      {editing && (
        <div ref={panelRef} className="mb-4">
          <SeriesEditPanel
            key={editing.id ?? "new"}
            saved={editing}
            companyId={companyId}
            onSaved={closeAndReload}
            onCancel={() => setEditing(null)}
          />
        </div>
      )}
      <CatalogTable
        columns={[
          { key: "title", header: t("series_prefix") },
          { key: "description", header: t("description") },
        ]}
        rows={collection.map((series) => ({
          id: series.id,
          cells: {
            title: <span className="font-medium">{series.title}</span>,
            description: series.description,
          },
        }))}
        renderRowAction={(id) => <ChangeLink onClick={() => startEdit(id)} />}
        empty={t("series_empty")}
        isLoading={isLoading}
        loadError={isError ? t("settings_load_failed") : undefined}
      />
      <p className="mt-3 text-sm text-gray-600">
        <Trans
          i18nKey="series_invoice_numbering_note"
          components={{
            salesLink: (
              <Link
                to={`/app/${companyId}/settings/sales`}
                className="font-medium text-indigo-600 hover:text-indigo-800"
              />
            ),
          }}
        />
      </p>
    </SettingsCard>
  );
}

export default SeriesContainer;
